import type {
  ChannelModelRecord,
  ChannelRecord,
  RoutingBreakerRecord,
  RoutingBreakerState,
  RoutingConfigRecord,
  RoutingPolicyMode,
  RoutingSkuTargetRecord,
  TargetProbeLayer,
  TargetProbeStateRecord,
} from '@nadoutong/contracts';

/**
 * Shared helpers for the tenant-routing surface (config library page +
 * detail sheet + target dialog, epic #886). Pure functions and label-key
 * maps only — no React in here.
 */

export const POLICY_MODE_OPTIONS: readonly RoutingPolicyMode[] = [
  'full_chain',
  'network_only',
  'same_cache_domain',
  'fail_fast',
] as const;

export type PolicyModeLabelKey =
  | 'tenant_routing.policy_mode.full_chain'
  | 'tenant_routing.policy_mode.network_only'
  | 'tenant_routing.policy_mode.same_cache_domain'
  | 'tenant_routing.policy_mode.fail_fast';

export type PolicyModeHintKey =
  | 'tenant_routing.policy_mode.full_chain_hint'
  | 'tenant_routing.policy_mode.network_only_hint'
  | 'tenant_routing.policy_mode.same_cache_domain_hint'
  | 'tenant_routing.policy_mode.fail_fast_hint';

export function policyModeLabelKey(mode: RoutingPolicyMode): PolicyModeLabelKey {
  switch (mode) {
    case 'network_only':
      return 'tenant_routing.policy_mode.network_only';
    case 'same_cache_domain':
      return 'tenant_routing.policy_mode.same_cache_domain';
    case 'fail_fast':
      return 'tenant_routing.policy_mode.fail_fast';
    case 'full_chain':
      return 'tenant_routing.policy_mode.full_chain';
  }
}

export function policyModeHintKey(mode: RoutingPolicyMode): PolicyModeHintKey {
  switch (mode) {
    case 'network_only':
      return 'tenant_routing.policy_mode.network_only_hint';
    case 'same_cache_domain':
      return 'tenant_routing.policy_mode.same_cache_domain_hint';
    case 'fail_fast':
      return 'tenant_routing.policy_mode.fail_fast_hint';
    case 'full_chain':
      return 'tenant_routing.policy_mode.full_chain_hint';
  }
}

export function routeConfigTypeLabelKey(type: RoutingConfigRecord['config_type']):
  | 'tenant_routing.configs.type.system'
  | 'tenant_routing.configs.type.customer'
  | 'tenant_routing.configs.type.experiment' {
  switch (type) {
    case 'customer':
      return 'tenant_routing.configs.type.customer';
    case 'experiment':
      return 'tenant_routing.configs.type.experiment';
    case 'system':
      return 'tenant_routing.configs.type.system';
  }
}

export function routeConfigStatusLabelKey(status: RoutingConfigRecord['status']):
  | 'tenant_routing.configs.status.draft'
  | 'tenant_routing.configs.status.active'
  | 'tenant_routing.configs.status.archived' {
  switch (status) {
    case 'draft':
      return 'tenant_routing.configs.status.draft';
    case 'archived':
      return 'tenant_routing.configs.status.archived';
    case 'active':
      return 'tenant_routing.configs.status.active';
  }
}

/**
 * #1919. Whether a routing config carries the soft deprecation marker.
 *
 * "Carries a timestamp", not "is not null": a projection that omits the field
 * yields `undefined`, and treating absent-as-marked would hide a live config
 * from every picker. Both this and the gateway's assign-org guard fail OPEN —
 * an unreadable marker must never remove an operator's options.
 *
 * Lives here rather than in `TenantRoutingPage` because four surfaces have to
 * agree on it: the config library, the assign sheet, the service-level anchor
 * picker and the customer-360 route switcher. A second copy written as
 * `!== null` would be wrong in the `undefined` case, which is precisely how
 * this predicate gets re-derived incorrectly.
 *
 * Callers filtering a PICKER must keep the currently-selected value visible
 * even when it is deprecated: the marker blocks NEW bindings, and dropping an
 * existing one from the option list makes the control read as unbound and can
 * silently unbind it on save.
 */
export function isDeprecated(config: { readonly deprecated_at?: string | null }): boolean {
  return typeof config.deprecated_at === 'string';
}

export function parseNonNegativeInteger(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0 || !/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function parsePositiveInteger(raw: string): number | null {
  const value = parseNonNegativeInteger(raw);
  return value !== null && value > 0 ? value : null;
}

/**
 * `''` → explicit `null` (auto-ranked by weight); digits → positive
 * integer; anything else → `undefined` (invalid input, caller rejects).
 */
export function parseOptionalRetryOrder(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const value = parsePositiveInteger(trimmed);
  return value === null ? undefined : value;
}

/**
 * Identity line for one SKU pool row (epic #898 P5). Mirrors
 * `formatChannelTargetIdentity`, but the SKU wire record carries raw ids
 * only — labels are not join-derived on this endpoint.
 */
export function formatSkuTargetIdentity(
  target: Pick<RoutingSkuTargetRecord, 'upstream_id' | 'account_id' | 'key_bucket_id'>,
): string {
  return `${target.upstream_id} · ${target.account_id} · ${target.key_bucket_id}`;
}

/** Longest `<prefix>_` still worth keeping; past it the id is all body. */
const SHORT_ID_PREFIX_MAX = 8;
/** Characters of the id body kept, verbatim, from the end. */
const SHORT_ID_TAIL = 6;

/**
 * Eyeball-matchable short form of an opaque id (#1494): the type prefix plus
 * the LAST `SHORT_ID_TAIL` characters, verbatim — `ups_bebf…-bf05c94aafd9`
 * becomes `ups_…4aafd9`.
 *
 * Two properties are load-bearing:
 *
 *   - The tail is a literal suffix of the input, never a digest. The whole
 *     point is that an operator who expands the disclosure can match the short
 *     form against the full id by eye; a hash of the id would look like an id
 *     and point at nothing.
 *   - The output length is bounded by construction (≤ 15 chars) regardless of
 *     input length, because the prefix is only kept when the separator lands
 *     inside `SHORT_ID_PREFIX_MAX`. A row's front face therefore cannot grow
 *     with the data — which is the reason this exists rather than a CSS
 *     truncation of the full id.
 */
export function shortIdentifier(id: string): string {
  const separator = id.indexOf('_');
  const prefix = separator >= 0 && separator < SHORT_ID_PREFIX_MAX ? id.slice(0, separator + 1) : '';
  const body = id.slice(prefix.length);
  return body.length <= SHORT_ID_TAIL ? id : `${prefix}…${body.slice(-SHORT_ID_TAIL)}`;
}

/** Separator between identity segments, shared by every surface that shows them. */
const IDENTITY_SEPARATOR = ' · ';

/**
 * A target's identity as ordered, already-shortened segments — the thing that
 * actually distinguishes one pool row from another.
 *
 * A pool row is a (upstream, account, key bucket) tuple, so all three are
 * needed to tell two rows apart in the worst case: splitting one account's
 * keys into two buckets for rate-limit isolation is a real configuration, and
 * those two rows differ ONLY in the last segment.
 *
 * `head` is what the surface leads with. The row face leads with the upstream
 * id; the share legend leads with the human-readable channel slug. Everything
 * after it is identical, which is the point of sharing this: the legend and
 * the row must not disagree about what identifies a target.
 */
export function targetIdentitySegments(
  target: Pick<RoutingSkuTargetRecord, 'upstream_id' | 'account_id' | 'key_bucket_id'>,
  head: string = shortIdentifier(target.upstream_id),
): readonly string[] {
  return [head, shortIdentifier(target.account_id), shortIdentifier(target.key_bucket_id)];
}

export function joinIdentitySegments(segments: readonly string[]): string {
  return segments.join(IDENTITY_SEPARATOR);
}

export type SkuTargetIdentifierLabelKey =
  | 'tenant_routing.default.upstream'
  | 'tenant_routing.default.account'
  | 'tenant_routing.default.key_bucket'
  | 'tenant_routing.default.upstream_channel'
  | 'tenant_routing.default.capability';

export interface SkuTargetIdentifier {
  readonly labelKey: SkuTargetIdentifierLabelKey;
  readonly value: string;
}

/**
 * Every opaque id a pool row stands for, labelled (#1494). This is what the
 * row's disclosure lists: the five classes (`ups_` / `ua_` / `ukb_` / `ch_` /
 * `cap_`) that used to be printed on the row's face, where three of them were
 * identical on every row of a namespace and none of them were readable.
 *
 * `capability_id` is dropped — not blank — on legacy unpinned rows, because
 * there is no id to read; the face carries the legacy chip instead. The wire
 * value there is an explicit `null` (the contract marks the field required);
 * the check is `== null` only so a fixture that omitted the key cannot render
 * the string "undefined" as an identifier.
 */
export function skuTargetIdentifiers(
  target: Pick<
    RoutingSkuTargetRecord,
    'upstream_id' | 'account_id' | 'key_bucket_id' | 'upstream_channel_id' | 'capability_id'
  >,
): readonly SkuTargetIdentifier[] {
  const identifiers: SkuTargetIdentifier[] = [
    { labelKey: 'tenant_routing.default.upstream', value: target.upstream_id },
    { labelKey: 'tenant_routing.default.account', value: target.account_id },
    { labelKey: 'tenant_routing.default.key_bucket', value: target.key_bucket_id },
    { labelKey: 'tenant_routing.default.upstream_channel', value: target.upstream_channel_id },
  ];
  return target.capability_id == null
    ? identifiers
    : [...identifiers, { labelKey: 'tenant_routing.default.capability', value: target.capability_id }];
}

/**
 * One selectable SKU (epic #898): a `namespace/model` row of the
 * channel-models catalog joined with its namespace channel record.
 * Built from the `useChannelModelsList` + `useChannelsList` hook outputs
 * (this module stays React-free, so callers pass the loaded data in).
 */
export interface SkuOption {
  readonly skuId: string;
  readonly skuName: string;
  readonly vendor: string;
  readonly model: string;
  readonly channelId: string;
}

/** SKUs of one namespace (platform channel), in stable model order. */
export interface NamespaceSkuGroup {
  readonly channel: ChannelRecord;
  readonly skus: readonly SkuOption[];
}

export function buildSkuOptions(
  channelModels: readonly ChannelModelRecord[],
  channels: readonly ChannelRecord[],
): SkuOption[] {
  const channelById = new Map(channels.map((channel) => [channel.id, channel]));
  const options: SkuOption[] = [];
  for (const row of channelModels) {
    const channel = channelById.get(row.channel_id);
    if (channel === undefined) continue;
    options.push({
      skuId: row.sku_id,
      skuName: row.sku_name,
      vendor: channel.vendor,
      model: row.model,
      channelId: channel.id,
    });
  }
  return options.sort((a, b) =>
    a.vendor.localeCompare(b.vendor) ||
    a.skuName.localeCompare(b.skuName) ||
    a.model.localeCompare(b.model),
  );
}

/**
 * Group the SKU catalog by namespace (platform channel) preserving the
 * channel order the caller provides. Namespaces without SKUs are kept —
 * the matrix renders them as empty groups so a bare namespace is visible.
 */
export function buildNamespaceSkuGroups(
  channelModels: readonly ChannelModelRecord[],
  channels: readonly ChannelRecord[],
): NamespaceSkuGroup[] {
  const options = buildSkuOptions(channelModels, channels);
  return channels.map((channel) => ({
    channel,
    skus: options.filter((option) => option.channelId === channel.id),
  }));
}

/**
 * Retry-chain display order for a SKU pool (epic #898): explicit
 * `retry_order` ASC (NULLs last) → `weight` DESC → `priority` DESC →
 * `id` ASC. Mirrors the #908 selector's SKU-partition ranking so
 * operators read the pool in forward attempt order.
 */
export function sortSkuPoolTargets<
  T extends Pick<RoutingSkuTargetRecord, 'id' | 'weight' | 'retry_order' | 'priority'>,
>(
  targets: readonly T[],
): T[] {
  return [...targets].sort((a, b) => {
    if (a.retry_order !== null || b.retry_order !== null) {
      if (a.retry_order === null) return 1;
      if (b.retry_order === null) return -1;
      if (a.retry_order !== b.retry_order) return a.retry_order - b.retry_order;
    }
    if (b.weight !== a.weight) return b.weight - a.weight;
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.id.localeCompare(b.id);
  });
}

/**
 * What a PUT proposes, reduced to the columns `upsertSkuTarget` looks rows up
 * by: the physical four-tuple, plus the capability the write would pin.
 *
 * Structural on purpose: the shapes that can be PUT are a supply candidate and
 * a target-dialog submission, and they share nothing but these fields. Typing
 * the check against either concrete shape would have forced a second copy of
 * the predicate for the other, and the two copies would drift.
 */
export interface SupplyUpsertKey {
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  readonly capability_id: string;
}

/**
 * The row this SKU already owns that `upsertSkuTarget` would UPDATE instead of
 * inserting beside — `null` when the PUT really is an insert.
 *
 * This is a transcription of the server's own lookup, and it must stay one:
 *
 * ```sql
 * WHERE sku_id=$1 AND upstream_id=$2 AND account_id=$3 AND key_bucket_id=$4
 *   AND upstream_channel_id=$5 AND (capability_id = $6 OR capability_id IS NULL)
 * ORDER BY capability_id IS NULL ASC
 * ```
 *
 * …followed by `targetId = before?.id ?? id`. BOTH arms of that OR relocate an
 * existing binding under the banner of "add a target" — row count unchanged,
 * success toast — so a guard that reads only one arm closes only half the hole
 * (#1431). The `ORDER BY` puts the pinned arm first, which is why it is
 * checked first here.
 *
 * Scope-agnostic by construction: the default-pool lookup and the per-config
 * lookup differ only by the leading `config_id` predicate (#1457), which each
 * caller has already applied by handing in the rows of ONE scope. Everything
 * after that is the same OR over the same columns, so both scopes share this
 * predicate rather than keeping two copies of it.
 *
 * The two arms are different accidents and get different remedies, so callers
 * branch on `capability_id`:
 *
 *   - `null` (legacy, unpinned) → the repair is to pin that row, which carries
 *     the SERVER's weight rather than a draft's.
 *   - equal to the submitted capability → refuse. There is nothing to repair:
 *     that binding already exists and is editable in place, which is the
 *     correct entry point for changing its weight.
 *
 * A four-tuple carrying BOTH kinds of row is not a silent-corruption case:
 * the server's `maybeOne` sees two rows and throws `PgCardinalityError` → 500.
 * Loud, and not what this guard is defending. The silent one is the four-tuple
 * that carries only the pinned row.
 */
export function findRowMatchedByUpsert(
  targets: readonly RoutingSkuTargetRecord[],
  submission: SupplyUpsertKey,
): RoutingSkuTargetRecord | null {
  const onTuple = targets.filter((target) =>
    target.upstream_id === submission.upstream_id &&
    target.account_id === submission.account_id &&
    target.key_bucket_id === submission.key_bucket_id &&
    target.upstream_channel_id === submission.upstream_channel_id);
  return onTuple.find((target) => target.capability_id === submission.capability_id)
    ?? onTuple.find((target) => target.capability_id === null)
    ?? null;
}

/** Probe state for this pool row: `(layer, target_id)`, not target id alone. */
export function probeStateForTarget(
  states: readonly TargetProbeStateRecord[],
  target: Pick<RoutingSkuTargetRecord, 'id'>,
  layer: TargetProbeLayer,
): TargetProbeStateRecord | null {
  return states.find((row) => row.target_id === target.id && row.layer === layer) ?? null;
}

export function breakerForTarget(
  breakers: readonly RoutingBreakerRecord[],
  target: Pick<
    RoutingSkuTargetRecord,
    'upstream_id' | 'account_id' | 'key_bucket_id' | 'upstream_channel_id'
  >,
): RoutingBreakerRecord | null {
  return breakers.find((breaker) =>
    breaker.upstream_id === target.upstream_id &&
    breaker.account_id === target.account_id &&
    breaker.key_bucket_id === target.key_bucket_id &&
    breaker.upstream_channel_id === target.upstream_channel_id,
  ) ?? null;
}

/** Open + forced entries — the health-strip count (half-open is recovering). */
export function countBlockingBreakers(breakers: readonly RoutingBreakerRecord[]): number {
  return breakers.filter((breaker) => breaker.state === 'open' || breaker.state === 'forced').length;
}

/**
 * Force-open (trip) duration presets shared by the breakers drawer and
 * the config-sheet row action. UI-only sugar: the wire contract stays an
 * arbitrary validated ISO `until` timestamp (max 24h ahead).
 */
export const TRIP_DURATION_OPTIONS = ['30m', '1h', '6h', '24h'] as const;
export type TripDuration = (typeof TRIP_DURATION_OPTIONS)[number];

export const TRIP_DURATION_MS: Readonly<Record<TripDuration, number>> = {
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
};

export type TripDurationLabelKey =
  | 'tenant_routing.breaker.trip_duration.30m'
  | 'tenant_routing.breaker.trip_duration.1h'
  | 'tenant_routing.breaker.trip_duration.6h'
  | 'tenant_routing.breaker.trip_duration.24h';

export function tripDurationLabelKey(duration: TripDuration): TripDurationLabelKey {
  switch (duration) {
    case '30m':
      return 'tenant_routing.breaker.trip_duration.30m';
    case '1h':
      return 'tenant_routing.breaker.trip_duration.1h';
    case '6h':
      return 'tenant_routing.breaker.trip_duration.6h';
    case '24h':
      return 'tenant_routing.breaker.trip_duration.24h';
  }
}

export type BreakerStateLabelKey =
  | 'tenant_routing.breaker.state.open'
  | 'tenant_routing.breaker.state.half_open'
  | 'tenant_routing.breaker.state.forced';

export function breakerStateLabelKey(state: RoutingBreakerState): BreakerStateLabelKey {
  switch (state) {
    case 'half_open':
      return 'tenant_routing.breaker.state.half_open';
    case 'forced':
      return 'tenant_routing.breaker.state.forced';
    case 'open':
      return 'tenant_routing.breaker.state.open';
  }
}

/** Labeled identity line for a breaker row (mirrors the pool row format). */
export function formatBreakerIdentity(record: RoutingBreakerRecord): string {
  const parts = [
    record.upstream_name ?? record.upstream_id,
    record.account_label ?? record.account_id,
    record.key_bucket_label ?? record.key_bucket_id,
    record.upstream_channel_slug ?? record.upstream_channel_id,
  ];
  return parts.filter((part): part is string => part !== null && part.length > 0).join(' · ');
}

/** Cooldown remaining in whole seconds (floored, never negative). */
export function breakerRemainingSeconds(cooldownRemainingMs: number | null): number {
  if (cooldownRemainingMs === null) return 0;
  return Math.max(0, Math.floor(cooldownRemainingMs / 1000));
}
